/**
 * Claim snapshots - captured synchronously when WORD_CLAIMED arrives, BEFORE
 * the store removes stolen words, so animations know where the stolen letters
 * were on screen.
 */

export interface SnapshotRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface StolenWordSnapshot {
  playerId: string;
  wordId: string;
  letters: string[];
  rect: SnapshotRect | null;
}

export interface ClaimSnapshot {
  stolenWords: StolenWordSnapshot[];
  poolRect: SnapshotRect | null;
}

const snapshots = new Map<string, ClaimSnapshot>();
const MAX_SNAPSHOTS = 20;

function toRect(el: Element | null): SnapshotRect | null {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { left: r.left, top: r.top, width: r.width, height: r.height };
}

function findWordElement(wordId: string): Element | null {
  const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(wordId) : wordId.replace(/"/g, '\\"');
  return document.querySelector(`[data-word-id="${escaped}"]`);
}

/** Record the pre-claim positions/letters of stolen words for a claim */
export function recordClaimSnapshot(
  claimedWordId: string,
  stolen: { playerId: string; wordId: string; letters: string[] }[]
): void {
  snapshots.set(claimedWordId, {
    stolenWords: stolen.map((s) => ({ ...s, rect: toRect(findWordElement(s.wordId)) })),
    poolRect: toRect(document.querySelector('[data-tile-pool]')),
  });
  // Bound memory in case snapshots are never consumed (e.g. reduced motion)
  if (snapshots.size > MAX_SNAPSHOTS) {
    const oldest = snapshots.keys().next().value;
    if (oldest !== undefined) snapshots.delete(oldest);
  }
}

/** Retrieve (and remove) the snapshot for a claim */
export function takeClaimSnapshot(claimedWordId: string): ClaimSnapshot | null {
  const snap = snapshots.get(claimedWordId) ?? null;
  snapshots.delete(claimedWordId);
  return snap;
}
